import { useCallback, useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";

import useToastActionWrapper from "./useToastActionWrapper";
import { listExports, generateExport, getExportDownloadUrl } from "../services/datevExportsV2";
import { downloadFromUrl } from "../utils/fileDownload";
import {
  DatevAccountOverrides,
  DatevExportRow,
  DatevKind,
  DatevSource,
  MONTH_NAMES,
  slotKey,
} from "../pages/Accounting/datevExports/datevExportTypes";

interface UseDatevExportsParams {
  year: number;
  overrides: DatevAccountOverrides;
  toastRef: React.RefObject<Toast>;
}

interface UseDatevExports {
  rows: DatevExportRow[];
  isBusy: (key: string) => boolean;
  anyGenerating: boolean;
  generate: (month: number, source: DatevSource, kind: DatevKind) => void;
  download: (row: DatevExportRow) => void;
}

const POLL_INTERVAL_MS = 4000;

/**
 * Owns the DATEV exports grid data: loading, polling while any export is
 * GENERATING, and the generate/download side effects. Keeps the page focused
 * on rendering (per the repo's "data fetching in src/hooks/" convention).
 */
const useDatevExports = ({ year, overrides, toastRef }: UseDatevExportsParams): UseDatevExports => {
  const { toastActionWrapper } = useToastActionWrapper(toastRef);
  const [rows, setRows] = useState<DatevExportRow[]>([]);

  // Per-cell busy tracking: concurrent actions on different cells must not
  // clear each other's loading state or re-enable a still-in-flight button.
  const [busyKeys, setBusyKeys] = useState<Set<string>>(new Set());
  const setBusy = useCallback(
    (key: string) => setBusyKeys((prev) => new Set(prev).add(key)),
    []
  );
  const clearBusy = useCallback(
    (key: string) =>
      setBusyKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      }),
    []
  );
  const isBusy = useCallback((key: string) => busyKeys.has(key), [busyKeys]);

  // In-flight GENERATE requests only (kept separate from busyKeys, which also
  // tracks downloads) so the global generate-lock isn't tripped by a download.
  const [generateInFlight, setGenerateInFlight] = useState(0);

  // Guard against out-of-order listExports(year) responses: a slower earlier
  // request must not overwrite rows after the year (or a later poll) changed.
  const loadSeqRef = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    const data = await listExports(year);
    if (seq !== loadSeqRef.current) return;
    setRows(data);
  }, [year]);

  // Initial load: surface failures so an empty grid isn't mistaken for "no
  // exports yet" (which would prompt redundant regeneration). Background polls
  // below stay silent — a failed poll shouldn't spam toasts.
  useEffect(() => {
    load().catch(() =>
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: `Failed to load DATEV exports for ${year}.`,
        life: 3000,
      })
    );
  }, [load, toastRef, year]);

  // Poll while any visible export is GENERATING (server-side state).
  useEffect(() => {
    if (!rows.some((r) => r.status === "GENERATING")) return undefined;
    const id = setInterval(() => {
      load().catch(() => undefined);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [rows, load]);

  const upsertRow = useCallback(
    (updated: DatevExportRow) =>
      setRows((prev) => {
        const k = slotKey(updated.month, updated.source, updated.kind);
        const without = prev.filter((r) => slotKey(r.month, r.source, r.kind) !== k);
        return [...without, updated];
      }),
    []
  );

  const generate = useCallback(
    (month: number, source: DatevSource, kind: DatevKind) => {
      const key = slotKey(month, source, kind);
      setBusy(key);
      setGenerateInFlight((n) => n + 1);
      toastActionWrapper({
        action: async () => {
          const updated = await generateExport({ year, month, source, kind, ...overrides });
          upsertRow(updated);
          return true;
        },
        successMessage: `${source} ${kind} for ${MONTH_NAMES[month - 1]} ${year} ${kind === "receipts" ? "started" : "generated"}.`,
        errorMessage: `Failed to generate ${source} ${kind} for ${MONTH_NAMES[month - 1]} ${year}.`,
      }).finally(() => {
        clearBusy(key);
        setGenerateInFlight((n) => n - 1);
      });
    },
    [toastActionWrapper, year, overrides, upsertRow, setBusy, clearBusy]
  );

  const download = useCallback(
    (row: DatevExportRow) => {
      const key = slotKey(row.month, row.source, row.kind);
      setBusy(key);
      toastActionWrapper({
        action: async () => {
          const url = await getExportDownloadUrl(row.exportId);
          downloadFromUrl(url);
          return true;
        },
        successMessage: `Download started.`,
        errorMessage: `Failed to get the download link.`,
      }).finally(() => clearBusy(key));
    },
    [toastActionWrapper, setBusy, clearBusy]
  );

  const anyGenerating = generateInFlight > 0 || rows.some((r) => r.status === "GENERATING");

  return { rows, isBusy, anyGenerating, generate, download };
};

export default useDatevExports;
